import {
  Account,
  FeatureName,
  PermissionValues as Permissions,
  WorkspaceType,
} from '@gripp/shared-logic';
import { IconName } from '@gripp/shared-ui';
import { FC } from 'react';
import { AssetMenuItem } from '../../assets/components/assetsMenuItem';
import { HelpMenuItem } from '../../help/components/helpMenuItem';
import { NavLinkProps } from './menuLink';

export interface IMenu {
  titleKey: string;
  icon: IconName | string;
  route?: string;
  requiredPermission: string;
  requiredFeature?: FeatureName;
  requiredWorkspaceType?: WorkspaceType;
  isWorkspaceRequired?: boolean | ((account: Account | null) => boolean);
  component?: FC<NavLinkProps>;
}

export enum Routes {
  AccountPage = 'account-page',
  Accounts = 'accounts',
  Home = 'home',
  Activities = 'activities',
  Assets = 'assets',
  Campaigns = 'campaigns',
  Groups = 'groups',
  Routines = 'routines',
  NewRoutines = 'new-routines',
  Tags = 'tags',
  TagBatches = 'tagbatches',
  Workspaces = 'workspaces',
  Features = 'features',
  AlertsManagement = 'alerts-management',
  Products = 'products',
  Files = 'files',
  AddOns = 'add-ons',
  Settings = 'settings',
}

export const routeNames = {
  [Routes.AccountPage]: `/account-page`,
  [Routes.Accounts]: `/accounts`,
  [Routes.Home]: `/home`,
  [Routes.Activities]: `/activities`,
  [Routes.Assets]: `/assets`,
  [Routes.Campaigns]: `/campaigns`,
  [Routes.Groups]: `/groups`,
  [Routes.Routines]: `/routines`,
  [Routes.NewRoutines]: `/new-routines`,
  [Routes.Products]: `/products`,
  [Routes.Files]: `/files`,
  [Routes.Tags]: `/tags`,
  [Routes.TagBatches]: `/tagbatches`,
  [Routes.Workspaces]: `/workspaces`,
  [Routes.Features]: `/features`,
  [Routes.AlertsManagement]: `/alerts-management`,
  [Routes.AddOns]: `/add-ons`,
  [Routes.Settings]: `/settings`,
};

const guestMenuItems: IMenu[] = [
  {
    titleKey: 'activity.menu.title',
    icon: 'Activity',
    route: routeNames[Routes.Activities],
    requiredPermission: Permissions.activity.read,
  },
  {
    titleKey: 'assets.menu.title',
    icon: 'QR',
    route: routeNames[Routes.Assets],
    requiredPermission: Permissions.asset.read,
    component: AssetMenuItem,
  },
  {
    titleKey: 'admin.menu.admin',
    icon: 'People',
    route: routeNames[Routes.Accounts],
    requiredPermission: Permissions.peoplepage.read,
    isWorkspaceRequired: (account: Account | null) => !account?.isGlobalAdmin(),
  },
];

const adminMenuItems: IMenu[] = [
  {
    titleKey: 'routine.list.routines',
    icon: 'Routine',
    route: routeNames[Routes.NewRoutines],
    requiredPermission: Permissions.routinespage.read,
    requiredFeature: FeatureName.NewRoutinesPage,
  },
  {
    titleKey: 'tags.title',
    icon: 'Remove',
    route: routeNames[Routes.Tags],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'tags.batches.title',
    icon: 'Remove',
    route: routeNames[Routes.TagBatches],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'routine.list.oldRoutines',
    icon: 'Remove',
    route: routeNames[Routes.Routines],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'features.title',
    icon: 'Remove',
    route: routeNames[Routes.Features],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'alertsManagement.menu.title',
    icon: 'Alert',
    route: routeNames[Routes.AlertsManagement],
    requiredPermission: Permissions.globalAdmin,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'admin.menu.workspaces',
    icon: 'Remove',
    route: routeNames[Routes.Workspaces],
    requiredPermission: Permissions.globalAdmin,
  },
  {
    titleKey: 'help.menu.title',
    icon: 'Help',
    requiredPermission: Permissions.account.read,
    component: HelpMenuItem,
  },
];

const primaryAdminMenuItems: IMenu[] = [
  {
    titleKey: 'groups.menu.title',
    icon: 'Groups',
    route: routeNames[Routes.Groups],
    requiredPermission: Permissions.grouppage.read,
    requiredFeature: FeatureName.GroupsPage,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'product.list.products',
    icon: 'Tag',
    route: routeNames[Routes.Products],
    requiredPermission: Permissions.product.all,
    requiredWorkspaceType: WorkspaceType.Oem,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'campaigns.menu.title',
    icon: 'Campaigns',
    route: routeNames[Routes.Campaigns],
    requiredPermission: Permissions.campaign.read,
    requiredFeature: FeatureName.CampaignsPage,
    requiredWorkspaceType: WorkspaceType.Oem,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'addOn.list.addOns',
    icon: 'Addons',
    route: routeNames[Routes.AddOns],
    requiredPermission: Permissions.addon.all,
    requiredWorkspaceType: WorkspaceType.Oem,
    isWorkspaceRequired: true,
  },
  {
    titleKey: 'file.list.files',
    icon: 'Document',
    route: routeNames[Routes.Files],
    requiredPermission: Permissions.filespage.read,
    requiredFeature: FeatureName.FilesPage,
    requiredWorkspaceType: WorkspaceType.Oem,
    isWorkspaceRequired: true,
  },
];

const supportMenuItems: IMenu[] = [
  {
    titleKey: 'accountPage.menu.settingsPage',
    icon: 'Settings',
    route: routeNames[Routes.Settings],
    requiredPermission: Permissions.none,
    isWorkspaceRequired: false,
  },
];

export const MenuItems: IMenu[][] = [
  primaryAdminMenuItems,
  guestMenuItems,
  adminMenuItems,
  supportMenuItems,
];

/**
 * The OEM Home Dashboard nav entry. Only ever rendered for OEM workspaces with
 * the `OemHomeDashboard` flag on (see `useMenuPermission`); the per-item
 * `requiredFeature`/`requiredWorkspaceType` guards in the menu filter make it
 * a no-op for everyone else even if the entry leaks into a list.
 */
const homeMenuItem: IMenu = {
  titleKey: 'home.menu.title',
  icon: 'Home',
  route: routeNames[Routes.Home],
  requiredPermission: Permissions.activity.read,
  requiredFeature: FeatureName.OemHomeDashboard,
  requiredWorkspaceType: WorkspaceType.Oem,
  isWorkspaceRequired: true,
};

/**
 * Flag-on OEM nav order — a SINGLE explicit ordered list, consumed ONLY by
 * `useMenuPermission` when the OEM Home Dashboard flag is on for an OEM
 * workspace. It references the existing item objects so it stays in sync with
 * the shared groups; it does NOT mutate `primaryAdminMenuItems` /
 * `guestMenuItems` / `adminMenuItems`. Order mirrors the design reference
 * Sidebar (Home first, then Activity, Assets, Routines, People, Groups,
 * Products, Campaigns, Add-ons, Files), with **Customers omitted** (not a nav
 * entity yet). Items the reference doesn't enumerate (global-admin tools, Help)
 * are appended in their existing relative order so nothing silently disappears.
 *
 * Settings stays in its own trailing group because `MenuBase` renders the LAST
 * group separately in the sidebar's bottom section.
 */
const byTitleKey = (items: IMenu[], titleKey: string): IMenu => {
  const item = items.find((i) => i.titleKey === titleKey);
  if (!item) {
    throw new Error(`oemHomeMenuItems: missing menu item "${titleKey}"`);
  }
  return item;
};

const oemHomeTopMenuItems: IMenu[] = [
  homeMenuItem,
  byTitleKey(guestMenuItems, 'activity.menu.title'),
  byTitleKey(guestMenuItems, 'assets.menu.title'),
  byTitleKey(adminMenuItems, 'routine.list.routines'),
  byTitleKey(guestMenuItems, 'admin.menu.admin'),
  byTitleKey(primaryAdminMenuItems, 'groups.menu.title'),
  byTitleKey(primaryAdminMenuItems, 'product.list.products'),
  // Customers — intentionally omitted (not a nav entity in Phase 1).
  byTitleKey(primaryAdminMenuItems, 'campaigns.menu.title'),
  byTitleKey(primaryAdminMenuItems, 'addOn.list.addOns'),
  byTitleKey(primaryAdminMenuItems, 'file.list.files'),
  // Remaining global-admin tools + Help, in their existing relative order.
  byTitleKey(adminMenuItems, 'tags.title'),
  byTitleKey(adminMenuItems, 'tags.batches.title'),
  byTitleKey(adminMenuItems, 'routine.list.oldRoutines'),
  byTitleKey(adminMenuItems, 'features.title'),
  byTitleKey(adminMenuItems, 'alertsManagement.menu.title'),
  byTitleKey(adminMenuItems, 'admin.menu.workspaces'),
  byTitleKey(adminMenuItems, 'help.menu.title'),
];

export const OemHomeMenuItems: IMenu[][] = [
  oemHomeTopMenuItems,
  supportMenuItems,
];
